import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateSpace,
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  reserveRatePlanFieldErrors,
  reserveRatePlanFieldHelperText,
} from "../../../constants/reserveValidationHelperText";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  RESERVE_DND_CODE_KEY,
  RESERVE_DND_NAME_KEY,
  RESERVE_RATE_PLAN_ACTIVITY_NAME_KEY,
  RESERVE_RATE_PLAN_BASE_PRICE_KEY,
  RESERVE_RATE_PLAN_DAILY_WEEKLY_KEY,
  RESERVE_RATE_PLAN_GUEST_TYPE_PRICING_KEY,
  RESERVE_RATE_PLAN_HOURLY_PRICE_KEY,
} from "../../../constants/ReserveListScreensFields";
import {
  reserveListAvailableHeadingTooltip,
  reserveRatePlanTooltip,
} from "../../../constants/ReserveTooltipInformation";
import useScrollToTop from "../../hooks/useScrollToTop";

const RATE_PLAN_TEXT_MAX = validationLength.departmentName;

const clearAllFieldErrors = (setters: {
  setActivityNameErr: (v: boolean) => void;
  setBasePriceErr: (v: boolean) => void;
  setGuestTypePricingErr: (v: boolean) => void;
  setHourlyPriceErr: (v: boolean) => void;
  setDailyWeeklyErr: (v: boolean) => void;
}) => {
  setters.setActivityNameErr(false);
  setters.setBasePriceErr(false);
  setters.setGuestTypePricingErr(false);
  setters.setHourlyPriceErr(false);
  setters.setDailyWeeklyErr(false);
};

const ReserveRatePlan = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [activityName, setActivityName] = useState<string | null>(null);
  const [basePrice, setBasePrice] = useState<string | null>(null);
  const [guestTypePricing, setGuestTypePricing] = useState<string | null>(null);
  const [hourlyPrice, setHourlyPrice] = useState<string | null>(null);
  const [dailyWeekly, setDailyWeekly] = useState<string | null>(null);
  const [activityNameErr, setActivityNameErr] = useState<boolean>(false);
  const [basePriceErr, setBasePriceErr] = useState<boolean>(false);
  const [guestTypePricingErr, setGuestTypePricingErr] =
    useState<boolean>(false);
  const [hourlyPriceErr, setHourlyPriceErr] = useState<boolean>(false);
  const [dailyWeeklyErr, setDailyWeeklyErr] = useState<boolean>(false);
  const [ratePlanInformation, setRatePlanInformation] = useState<any[]>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const activityTrim = (activityName ?? "").trim();
  const basePriceTrim = (basePrice ?? "").trim();
  const guestTypeTrim = (guestTypePricing ?? "").trim();
  const hourlyTrim = (hourlyPrice ?? "").trim();
  const dailyWeeklyTrim = (dailyWeekly ?? "").trim();

  const buildPayload = () => ({
    [RESERVE_RATE_PLAN_ACTIVITY_NAME_KEY]: activityTrim,
    [RESERVE_RATE_PLAN_BASE_PRICE_KEY]: basePriceTrim,
    [RESERVE_RATE_PLAN_GUEST_TYPE_PRICING_KEY]: guestTypeTrim,
    [RESERVE_RATE_PLAN_HOURLY_PRICE_KEY]: hourlyTrim,
    [RESERVE_RATE_PLAN_DAILY_WEEKLY_KEY]: dailyWeeklyTrim,
    [RESERVE_DND_CODE_KEY]: activityTrim,
    [RESERVE_DND_NAME_KEY]: [basePriceTrim, hourlyTrim, dailyWeeklyTrim]
      .filter(Boolean)
      .join(" | "),
  });

  const validateInput = (): boolean => {
    clearAllFieldErrors({
      setActivityNameErr,
      setBasePriceErr,
      setGuestTypePricingErr,
      setHourlyPriceErr,
      setDailyWeeklyErr,
    });

    let valid = true;

    if (!activityTrim) {
      setActivityNameErr(true);
      valid = false;
    } else if (
      !validateSpace(activityTrim, RATE_PLAN_TEXT_MAX, validationType.OTHER)
    ) {
      setActivityNameErr(true);
      valid = false;
    }

    if (!basePriceTrim) {
      setBasePriceErr(true);
      valid = false;
    } else if (
      !validateString(basePriceTrim, RATE_PLAN_TEXT_MAX, validationType.NAME)
    ) {
      setBasePriceErr(true);
      valid = false;
    }

    if (guestTypeTrim && guestTypeTrim.length > RATE_PLAN_TEXT_MAX) {
      setGuestTypePricingErr(true);
      valid = false;
    }

    if (
      hourlyTrim &&
      !validateString(hourlyTrim, RATE_PLAN_TEXT_MAX, validationType.NAME)
    ) {
      setHourlyPriceErr(true);
      valid = false;
    }

    if (dailyWeeklyTrim && dailyWeeklyTrim.length > RATE_PLAN_TEXT_MAX) {
      setDailyWeeklyErr(true);
      valid = false;
    }

    return valid;
  };

  const addRatePlan = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        ratePlanInformation ?? [],
        RESERVE_RATE_PLAN_ACTIVITY_NAME_KEY,
        activityTrim,
      )
    ) {
      setAlertMessage("Cannot have duplicate special meals");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    const payload = buildPayload();
    setRatePlanInformation((prev: any) =>
      updateData(
        Array.isArray(prev) && prev.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRatePlan(null);
    setEditClicked(false);
    setAlertMessage(`${ReservePropertyEnum.ratePlan} added successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const updateRatePlan = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        (ratePlanInformation ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        RESERVE_RATE_PLAN_ACTIVITY_NAME_KEY,
        activityTrim,
      )
    ) {
      setAlertMessage("Cannot have duplicate special meals");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    const payload = buildPayload();
    setRatePlanInformation((prev: any) =>
      (Array.isArray(prev) ? prev : []).map((item: any) =>
        String(item.id) === String(selectedId) ? { ...item, ...payload } : item,
      ),
    );
    editRatePlan(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${ReservePropertyEnum.ratePlan} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const editRatePlan = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    if (list == null) {
      setActivityName(null);
      setBasePrice(null);
      setGuestTypePricing(null);
      setHourlyPrice(null);
      setDailyWeekly(null);
      clearAllFieldErrors({
        setActivityNameErr,
        setBasePriceErr,
        setGuestTypePricingErr,
        setHourlyPriceErr,
        setDailyWeeklyErr,
      });
      return;
    }
    setActivityName(
      list[RESERVE_RATE_PLAN_ACTIVITY_NAME_KEY] != null
        ? String(list[RESERVE_RATE_PLAN_ACTIVITY_NAME_KEY])
        : list["Activity Name"] != null
          ? String(list["Activity Name"])
          : list[RESERVE_DND_CODE_KEY] != null
            ? String(list[RESERVE_DND_CODE_KEY])
            : null,
    );
    setBasePrice(
      list[RESERVE_RATE_PLAN_BASE_PRICE_KEY] != null
        ? String(list[RESERVE_RATE_PLAN_BASE_PRICE_KEY])
        : null,
    );
    setGuestTypePricing(
      list[RESERVE_RATE_PLAN_GUEST_TYPE_PRICING_KEY] != null
        ? String(list[RESERVE_RATE_PLAN_GUEST_TYPE_PRICING_KEY])
        : null,
    );
    setHourlyPrice(
      list[RESERVE_RATE_PLAN_HOURLY_PRICE_KEY] != null
        ? String(list[RESERVE_RATE_PLAN_HOURLY_PRICE_KEY])
        : null,
    );
    setDailyWeekly(
      list[RESERVE_RATE_PLAN_DAILY_WEEKLY_KEY] != null
        ? String(list[RESERVE_RATE_PLAN_DAILY_WEEKLY_KEY])
        : null,
    );
    clearAllFieldErrors({
      setActivityNameErr,
      setBasePriceErr,
      setGuestTypePricingErr,
      setHourlyPriceErr,
      setDailyWeeklyErr,
    });
  };

  const deleteRatePlan = (id: number) => {
    setRatePlanInformation(deleteItem(ratePlanInformation ?? [], id));
  };

  useEffect(() => {
    setRatePlanInformation(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(ratePlanInformation);
    editClicked &&
      setSelectedId(
        checkData(ratePlanInformation, selectedId) ? selectedId : null,
      );
  }, [ratePlanInformation]);

  const activityNameHelperText = activityNameErr
    ? !activityTrim
      ? reserveRatePlanFieldErrors.required
      : reserveRatePlanFieldErrors.activityName
    : reserveRatePlanFieldHelperText.activityName;

  const basePriceHelperText = basePriceErr
    ? !basePriceTrim
      ? reserveRatePlanFieldErrors.required
      : reserveRatePlanFieldErrors.basePrice
    : reserveRatePlanFieldHelperText.basePrice;

  const guestTypePricingHelperText = guestTypePricingErr
    ? reserveRatePlanFieldErrors.guestTypePricing
    : reserveRatePlanFieldHelperText.guestTypePricing;

  const hourlyPriceHelperText = hourlyPriceErr
    ? reserveRatePlanFieldErrors.hourlyPrice
    : reserveRatePlanFieldHelperText.hourlyPrice;

  const dailyWeeklyHelperText = dailyWeeklyErr
    ? reserveRatePlanFieldErrors.dailyWeekly
    : reserveRatePlanFieldHelperText.dailyWeekly;

  type FieldRow = {
    id: string;
    label: string;
    value: string | null;
    setValue: (v: string | null) => void;
    clearError: () => void;
    tooltip: string;
    helperText: string;
    hasError: boolean;
    multiline?: boolean;
    placeholder?: string;
    required?: boolean;
  };

  const fieldRows: FieldRow[] = [
    {
      id: "reserve-rate-plan-special-meal",
      label: RESERVE_RATE_PLAN_ACTIVITY_NAME_KEY,
      value: activityName,
      setValue: setActivityName,
      clearError: () => setActivityNameErr(false),
      tooltip: reserveRatePlanTooltip.activityName,
      helperText: activityNameHelperText,
      hasError: activityNameErr,
      placeholder: "e.g. Vegan Lunch",
      required: true,
    },
    {
      id: "reserve-rate-plan-base-price",
      label: RESERVE_RATE_PLAN_BASE_PRICE_KEY,
      value: basePrice,
      setValue: setBasePrice,
      clearError: () => setBasePriceErr(false),
      tooltip: reserveRatePlanTooltip.basePrice,
      helperText: basePriceHelperText,
      hasError: basePriceErr,
      placeholder: "e.g. 50",
      required: true,
    },
    {
      id: "reserve-rate-plan-guest-type",
      label: RESERVE_RATE_PLAN_GUEST_TYPE_PRICING_KEY,
      value: guestTypePricing,
      setValue: setGuestTypePricing,
      clearError: () => setGuestTypePricingErr(false),
      tooltip: reserveRatePlanTooltip.guestTypePricing,
      helperText: guestTypePricingHelperText,
      hasError: guestTypePricingErr,
      placeholder: "e.g. 1.Adult - $100",
      multiline: true,
    },
    {
      id: "reserve-rate-plan-hourly",
      label: RESERVE_RATE_PLAN_HOURLY_PRICE_KEY,
      value: hourlyPrice,
      setValue: setHourlyPrice,
      clearError: () => setHourlyPriceErr(false),
      tooltip: reserveRatePlanTooltip.hourlyPrice,
      helperText: hourlyPriceHelperText,
      hasError: hourlyPriceErr,
      placeholder: "e.g. $30 for 1hr",
    },
    {
      id: "reserve-rate-plan-daily-weekly",
      label: RESERVE_RATE_PLAN_DAILY_WEEKLY_KEY,
      value: dailyWeekly,
      setValue: setDailyWeekly,
      clearError: () => setDailyWeeklyErr(false),
      tooltip: reserveRatePlanTooltip.dailyWeekly,
      helperText: dailyWeeklyHelperText,
      hasError: dailyWeeklyErr,
      multiline: true,
    },
  ];

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            gap="10px"
            alignItems="center"
            justifyContent="space-between"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              {ReservePropertyEnum.ratePlan}
              <Tooltip title={tooltipComponent(reserveRatePlanTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editRatePlan(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton click={() => editRatePlan(lastUpdatedRecord)} />
              )}
            </Stack>
          </Stack>

          {fieldRows.map((row) => (
            <Stack key={row.id} direction="column" width="100%" gap="5px">
              <FormLabel component="legend" required={row.required}>
                {row.label}
                <Tooltip title={tooltipComponent(row.tooltip)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id={row.id}
                variant="outlined"
                hiddenLabel
                placeholder={row.placeholder ?? row.label}
                helperText={row.helperText}
                value={row.value ?? ""}
                fullWidth
                multiline={row.multiline}
                minRows={row.multiline ? 3 : undefined}
                error={row.hasError}
                required={row.required}
                onChange={(e) => {
                  row.setValue(e.target.value);
                  row.clearError();
                }}
                inputProps={{ "aria-label": row.label }}
              />
            </Stack>
          ))}

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateRatePlan : addRatePlan}
              id="addReserveRatePlan"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
              {ReservePropertyEnum.ratePlan}
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography
              variant="h6"
              component="div"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {ReservePropertyEnum.ratePlan} available
              <Tooltip
                title={tooltipComponent(reserveListAvailableHeadingTooltip)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {Array.isArray(ratePlanInformation) &&
              ratePlanInformation.length > 0 ? (
                <DraggableItemList
                  itemData={ratePlanInformation}
                  onOrderUpdate={(order) => setRatePlanInformation(order)}
                  onEdit={(info) => {
                    editRatePlan(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRatePlan(id)}
                  code={RESERVE_DND_CODE_KEY}
                  name={RESERVE_DND_NAME_KEY}
                />
              ) : (
                <Typography variant="body2">
                  No {ReservePropertyEnum.ratePlan} information available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default ReserveRatePlan;
